import React from 'react';
import { useLocation } from '@reach/router';
import { Helmet } from 'react-helmet';
import favicon from 'assets/images/seo/favicon.svg';
import faviconApple from 'assets/images/seo/apple-touch-icon.png';
import faviconSafari from 'assets/images/seo/safari-pinned-tab.svg';
import og from 'assets/images/seo/ogp.png';
type SeoProps = {
  title?: string;
  description?: string;
  image?: string;
  locationPathName?: string;
};

const defaultTitle = {
  ja: 'FOURDIGIT Inc. | 株式会社フォーデジット',
  en: 'FOURDIGIT Inc.',
  th: 'FOURDIGIT Inc.',
};
const defaultDescription = {
  ja:
    'フォーデジットは、デジタルテクノロジーと人をつなぐ際に欠かせない「デザイン」を提供し、関わる人すべてが心地よい体験を作り上げるデザイン＆テックカンパニーです。デジタル化による企業の変革や、新サービス・新製品の体験設計、Webやアプリの制作・運用まで、幅広いデザインを提供します。',
  en:
    "FOURDIGIT is a design & technology company. We deliver designs that connect digital technology with people's lives to create experiences that feel good to everyone they touch.",
  th:
    'FOURDIGIT เป็นบริษัทออกแบบและเทคโนโลยี เราส่งมอบการออกแบบที่เชื่อมโยงเทคโนโลยีดิจิทัลกับชีวิตของผู้คน เพื่อสร้างประสบการณ์ที่ดีสำหรับทุกคนที่สัมผัส',
};
const defaultUrl = 'https://www.4digit.com';

export const SEO: React.FC<SeoProps> = props => {
  const location = useLocation ? useLocation() : undefined;
  const currentPath = props.locationPathName || location?.pathname || '/';

  const titleLang = {
    ja: props.title ? `${props.title}｜${defaultTitle.ja}` : defaultTitle.ja,
    en: props.title ? `${props.title}｜${defaultTitle.en}` : defaultTitle.en,
    th: props.title ? `${props.title}｜${defaultTitle.th}` : defaultTitle.th,
  };
  const descriptionLang = {
    ja: props.description ? `${props.description}、${defaultDescription.ja}` : defaultDescription.ja,
    en: props.description ? `${props.description},${defaultDescription.en}` : defaultDescription.en,
    th: props.description ? `${props.description},${defaultDescription.th}` : defaultDescription.th,
  };

  const isEN = currentPath.startsWith('/en/');
  const isTH = currentPath.startsWith('/th/');
  const lang = isTH ? 'th' : isEN ? 'en' : 'ja';
  const title = lang === 'th' ? titleLang.th : lang === 'en' ? titleLang.en : titleLang.ja.replace(/(<br>|<br \/>|<br\/>)/gi, '');
  const description = lang === 'th' ? descriptionLang.th : lang === 'en' ? descriptionLang.en : descriptionLang.ja;
  const image = props.image ? props.image : og;

  return (
    <Helmet
      title={title}
      htmlAttributes={{ lang }}
      bodyAttributes={{ 'data-lang': lang === 'th' ? 'th' : lang === 'en' ? 'en' : 'jp' }}
    >
      <meta name="description" content={description} />
      <meta name="image" content={image} />

      {/* canonical */}
      {currentPath && <link rel="canonical" href={defaultUrl + currentPath} />}

      {/* hreflang alternates */}
      {currentPath && (
        (() => {
          const p = currentPath || '/';
          const isEN = p.startsWith('/en/');
          const isTH = p.startsWith('/th/');
          const jaPath = isEN || isTH ? (p.slice(3) || '/') : p;
          const enPath = `/en${jaPath}`;
          const thPath = `/th${jaPath}`;
          return [
            <link key="alt-ja" rel="alternate" hrefLang="ja" href={defaultUrl + jaPath} />,
            <link key="alt-en" rel="alternate" hrefLang="en" href={defaultUrl + enPath} />,
            <link key="alt-th" rel="alternate" hrefLang="th" href={defaultUrl + thPath} />,
            <link key="alt-x" rel="alternate" hrefLang="x-default" href={defaultUrl + jaPath} />,
          ];
        })()
      )}

      {/* og */}
      {currentPath && <meta property="og:url" content={defaultUrl + currentPath} />}
      <meta property="og:type" content={props.title ? 'article' : 'website'} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={defaultUrl + image} />

      {/* facebook */}
      <meta property="fb:app_id" content="363718924756760" />

      {/* twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={defaultUrl + image} />

      {/* favicon */}
      <link rel="apple-touch-icon" sizes="200x200" href={faviconApple} />
      <link rel="icon" type="image/svg+xml" href={favicon} />
      <link rel="mask-icon" href={faviconSafari} color="#ffffff" />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta name="theme-color" content="#ffffff" />

      {/* adobe fonts. DNP Shuei Gothic Gin Std */}
      {lang === 'ja' && (
      <script type="text/javascript">
        {`
          (function(d) {
            var config = {
              kitId: 'exg7klz',
              scriptTimeout: 3000,
              async: true
            },
            h=d.documentElement,t=setTimeout(function(){h.className=h.className.replace(/\bwf-loading\b/g,"")+" wf-inactive";},config.scriptTimeout),tk=d.createElement("script"),f=false,s=d.getElementsByTagName("script")[0],a;h.className+=" wf-loading";tk.src='https://use.typekit.net/'+config.kitId+'.js';tk.async=true;tk.onload=tk.onreadystatechange=function(){a=this.readyState;if(f||a&&a!="complete"&&a!="loaded")return;f=true;clearTimeout(t);try{Typekit.load(config)}catch(e){}};s.parentNode.insertBefore(tk,s)
          })(document);
        `}
      </script>
      )}
      {lang === 'th' && (<link rel="preconnect" href="https://fonts.googleapis.com" /> )}
      {lang === 'th' && (<link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" /> )}
      {lang === 'th' && (<link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Thai:wght@100..900&family=Noto+Serif+Thai:wght@100..900&display=swap" rel="stylesheet" /> )}
    </Helmet>
  );
};
export default SEO;
